import { useEffect, useRef, useState } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useInView,
  animate,
} from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";

export const EASE = [0.16, 1, 0.3, 1];

export const Magnetic = ({ children, strength = 0.25 }) => {
  const ref = useRef(null);
  const x = useSpring(useMotionValue(0), { stiffness: 160, damping: 14 });
  const y = useSpring(useMotionValue(0), { stiffness: 160, damping: 14 });
  return (
    <motion.div
      ref={ref}
      className="inline-block"
      style={{ x, y }}
      onMouseMove={(e) => {
        const r = ref.current.getBoundingClientRect();
        x.set((e.clientX - r.left - r.width / 2) * strength);
        y.set((e.clientY - r.top - r.height / 2) * strength);
      }}
      onMouseLeave={() => {
        x.set(0);
        y.set(0);
      }}
    >
      {children}
    </motion.div>
  );
};

export const Reveal = ({ children, delay = 0, y = 36, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-8%" }}
    transition={{ duration: 0.9, delay, ease: EASE }}
    className={className}
  >
    {children}
  </motion.div>
);

const MaskLine = ({ children, delay }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10%" });
  return (
    <span ref={ref} className="-mb-[0.1em] block overflow-hidden pb-[0.1em]">
      <motion.span
        className="block will-change-transform"
        initial={{ y: "112%" }}
        animate={inView ? { y: "0%" } : {}}
        transition={{ duration: 1.05, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
};

export const MaskLines = ({ lines, className = "", delay = 0 }) => (
  <span className={className}>
    {lines.map((l, i) => (
      <MaskLine key={i} delay={delay + i * 0.1}>
        {l}
      </MaskLine>
    ))}
  </span>
);

export const Eyebrow = ({ children, tone = "stone", className = "" }) => {
  const color =
    tone === "champagne"
      ? "text-champagne"
      : tone === "dark"
        ? "text-obsidian/60"
        : "text-stone";
  return (
    <div
      className={`flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.32em] ${color} ${className}`}
    >
      <span
        className={`h-px w-8 ${tone === "champagne" ? "bg-champagne" : "bg-current opacity-60"}`}
      />
      <span>{children}</span>
    </div>
  );
};

export const CountUp = ({ to, duration = 1.8, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10%" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const c = animate(0, to, {
      duration,
      ease: EASE,
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => c.stop();
  }, [inView, to, duration]);
  return (
    <span ref={ref} className={className}>
      {val}
    </span>
  );
};

export const ArrowCta = ({
  children,
  onClick,
  href,
  dark = false,
  up = false,
  testId,
  className = "",
}) => {
  const Icon = up ? ArrowUpRight : ArrowRight;
  const inner = (
    <span
      className={`group inline-flex items-center gap-3 text-[12px] font-medium uppercase tracking-[0.22em] ${
        dark ? "text-obsidian" : "text-ivory"
      } ${className}`}
    >
      <span className="relative">
        {children}
        <span
          className={`absolute -bottom-1 left-0 h-px w-full origin-left scale-x-100 transition-transform duration-500 group-hover:scale-x-0 ${
            dark ? "bg-obsidian/40" : "bg-ivory/40"
          }`}
        />
        <span
          className={`absolute -bottom-1 left-0 h-px w-full origin-right scale-x-0 transition-transform duration-500 delay-100 group-hover:origin-left group-hover:scale-x-100 ${
            dark ? "bg-obsidian" : "bg-champagne"
          }`}
        />
      </span>
      <Icon
        size={15}
        className="transition-transform duration-500 group-hover:translate-x-1"
      />
    </span>
  );
  if (href)
    return (
      <a
        href={href}
        target={href.startsWith("http") ? "_blank" : undefined}
        rel="noreferrer"
        data-testid={testId}
      >
        {inner}
      </a>
    );
  return (
    <button type="button" onClick={onClick} data-testid={testId}>
      {inner}
    </button>
  );
};
